import { withJsonFormsControlProps } from '@jsonforms/react';
import { Stack, TextField, Typography } from '@rossum/ui/material';
import { useEffect, useRef } from 'react';

interface FStringControlProps {
  data: { __fstring: string } | string | undefined;
  handleChange(path: string, value: { __fstring: string }): void;
  path: string;
  label: string | undefined;
}

// Vibe coded
const highlightPattern = (text: string) => {
  const regex = /(?<!\\)\{(.*?)(?<!\\)\}/g; // Match unescaped {...}
  const parts = [];
  let lastIndex = 0;
  let match;

  // eslint-disable-next-line no-cond-assign
  while ((match = regex.exec(text)) !== null) {
    const start = match.index;
    const end = regex.lastIndex;

    // Push text before the match
    if (start > lastIndex) {
      parts.push(<span key={lastIndex}>{text.slice(lastIndex, start)}</span>);
    }

    // Push highlighted match
    parts.push(
      <Typography
        component="span"
        key={start}
        sx={{ color: theme => theme.palette.warning.main }}
      >
        {text.slice(start, end)}
      </Typography>
    );

    lastIndex = end;
  }

  // Push any remaining text
  if (lastIndex < text.length) {
    parts.push(<span key={lastIndex}>{text.slice(lastIndex)}</span>);
  }

  return parts;
};

export const FStringControl = withJsonFormsControlProps(
  ({ data, handleChange, path, label }: FStringControlProps) => {
    const value = data
      ? typeof data === 'string'
        ? data
        : data.__fstring
      : '';
    const inputRef = useRef<HTMLDivElement>(null);
    const highlightRef = useRef<HTMLElement>(null);

    // Sync scroll of overlay to input field
    useEffect(() => {
      const input = inputRef.current?.querySelector('input');
      const highlight = highlightRef.current;

      const syncScroll = () => {
        if (input && highlight) {
          highlight.scrollLeft = input.scrollLeft;
        }
      };

      input?.addEventListener('scroll', syncScroll);
      return () => input?.removeEventListener('scroll', syncScroll);
    }, []);

    return (
      <Stack sx={{ position: 'relative', mb: 2 }}>
        <Typography
          ref={highlightRef}
          sx={{
            width: 'auto !important',
            py: '16px',
            left: '14px',
            right: '14px',
            position: 'absolute',
            zIndex: 1000,
            pointerEvents: 'none',
            fontFamily: 'inherit',
            fontSize: '16px',
            whiteSpace: 'pre',
            overflow: 'hidden',
          }}
        >
          {highlightPattern(value ?? '')}
        </Typography>
        <TextField
          label={label}
          placeholder={label}
          ref={inputRef}
          fullWidth
          value={value}
          onChange={e => {
            handleChange(path, { __fstring: e.currentTarget.value });
          }}
          inputProps={{
            style: {
              position: 'relative',
              zIndex: 1,
            },
          }}
        />
      </Stack>
    );
  }
);
